import React, { useState } from "react";

import {
  List,
  DataTable,
  TextField,
  ReferenceField,
  NumberField,
  TextInput,
  ReferenceInput,
  Edit,
  SimpleForm,
  NumberInput,
  BooleanInput,
  EditButton,
  Create,
  required,
  number,
  Pagination,
  minValue,
  Show,
  TabbedShowLayout,
  Tab,
  AutocompleteInput,
  useRecordContext,
  Button,
  TopToolbar,
  CreateButton,
  useListContext,
  ReferenceManyField,
  SelectInput,
  FilterButton,
  BooleanField,
  ColumnsButton,
  useNotify,
} from "react-admin";

// Lib
import { custInfo } from "../lib/customer";
import { hasScope, SCOPE } from "../lib/auth";
import {
  VolumeField,
  ConvertGalToLitre,
  ConvertLitreToGal,
  _formatNum,
} from "../lib/volume";
import { DetailsButton } from "../lib/detailsButton";
import {
  MeasureOrAddField,
  MeasureOrAddDetailsButton,
} from "../lib/measureOrAddField";
import { CustomDatetimeField } from "../lib/datetime";

import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import { DownloadCSV } from "../lib/csvDownload";
import RangePickerInput from "../lib/dateRangePicker";
import { ChatBubbleTooltip } from "../lib/chatBubbleTooltip";

import { LotNameField } from "./lots";

// MUI
import IconDownload from "@mui/icons-material//CloudDownload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconCancel from "@mui/icons-material//Cancel";
import DialogActions from "@mui/material/DialogActions";
import { CustomTab } from "../lib/customTab";
import { VesselVintraceTransactionsTab } from "./vintrace_transactions";

const archivedFilterChoices = [
  { id: false, name: "Hide archived" },
  { id: true, name: "Show only archived" },
];

const barrelFilters = [
  <TextInput label="Vessel" source="search" alwaysOn resettable />,
  <TextInput label="Lot" source="currentLotName" alwaysOn resettable />,
  <SelectInput
    source="archived"
    label="Archived"
    alwaysOn
    choices={archivedFilterChoices}
    emptyText="Show all"
  />,
  <SelectInput
    source="type"
    choices={[
      { id: "barrel", name: "Barrel" },
      { id: "keg", name: "Keg" },
      { id: "tank", name: "Tank" },
      { id: "tirage", name: "Tirage" },
      { id: "sutter", name: "Sutter" },
      { id: "bin", name: "Bin" },
    ]}
  />,
];

const BarrelAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <span> {"Vessel " + (record.alias ? record.alias : record.barcode)}</span>
  );
};

const BarrelExportButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { sort, filterValues } = useListContext();
  const notify = useNotify();

  const handleDownload = async () => {
    setShowDialog(false);

    try {
      await DownloadCSV(
        "BarrelWise_Vessels",
        custInfo.customerID,
        "vessels",
        sort,
        filterValues,
      );
    } catch (err) {
      console.log(err);
      notify(`Failed to download CSV: ${err}`, { type: "error" });
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
        label="Export"
      >
        <IconDownload />
      </Button>
      <Dialog fullWidth open={showDialog}>
        <DialogTitle>CSV Export</DialogTitle>
        <DialogActions>
          <Button
            label="ra.action.cancel"
            variant="outlined"
            onClick={() => {
              setShowDialog(false);
            }}
          >
            <IconCancel />
          </Button>
          <Button label="Download" variant="contained" onClick={handleDownload}>
            <IconDownload />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const BarrelsListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <CreateButton disabled={!hasScope(SCOPE.MANAGER)} />
      <BarrelExportButton />
      <ColumnsButton />
    </TopToolbar>
  );
};

export const BarrelList = () => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={barrelFilters}
    filterDefaultValues={{ archived: false }}
    actions={<BarrelsListActions />}
    sort={{ field: "id", order: "ASC" }}
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="id" label="Vessel">
        <AliasOrBarcodeReference
          source="id"
          reference="vessels"
          label="Vessel"
        />
      </DataTable.Col>
      <DataTable.Col source="cooperID" label="Cooper">
        <ReferenceField
          source="cooperID"
          reference="barrel_cooper"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="year" label="Year">
        <TextField source="year" label="Year" />
      </DataTable.Col>
      <DataTable.Col source="capacity" label="Capacity">
        <VolumeField source="capacity" label="Capacity" />
      </DataTable.Col>
      <DataTable.Col source="lastFSO2Read.datetime" label="Last Analyzed">
        <CustomDatetimeField
          source="lastFSO2Read.datetime"
          label="Last Analyzed"
        />
      </DataTable.Col>
      <DataTable.Col source="lastFSO2Read.fso2" label="Free SO₂ (ppm)">
        <NumberField
          source="lastFSO2Read.fso2"
          options={{ maximumFractionDigits: 0 }}
          label={"Free SO\u2082 (ppm)"}
        />
      </DataTable.Col>
      <DataTable.Col source="currentLot.id" label="Lot">
        <ReferenceField source="currentLot.id" reference="lots" link="show">
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
      {hasScope(SCOPE.MANAGER) && (
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      )}
    </DataTable>
  </List>
);

const validateYear = [number(), minValue(2000)];
const validatePositiveNumber = [number(), minValue(0)];

const BarrelCreateEditForm = () => {
  const volumeUnit = custInfo.customerPreferences.volumeUnit;
  const capacityLabel =
    volumeUnit && volumeUnit === "Gal" ? "Capacity (Gal)" : "Capacity (L)";

  const volumeFormat = (value) => {
    if (value && volumeUnit === "Gal") {
      return _formatNum(ConvertLitreToGal(value), 2);
    }
    return value;
  };

  return (
    <SimpleForm>
      <TextInput source="barcode" label="Barcode" validate={required()} />
      <TextInput source="alias" label="Alias" defaultValue="" />
      <NumberInput
        source="capacity"
        label={capacityLabel}
        validate={validatePositiveNumber}
        format={volumeFormat}
      />
      <ReferenceInput
        source="cooperID"
        reference="barrel_cooper"
        sort={{ field: "name", order: "ASC" }}
      >
        <AutocompleteInput
          optionText="name"
          filterToQuery={(searchText) => ({ name: searchText })}
          label="Cooper"
        />
      </ReferenceInput>
      <ReferenceInput
        source="toastID"
        reference="barrel_toast"
        sort={{ field: "name", order: "ASC" }}
      >
        <AutocompleteInput
          optionText="name"
          filterToQuery={(searchText) => ({ name: searchText })}
          label="Toast"
        />
      </ReferenceInput>
      <ReferenceInput
        source="oakID"
        reference="barrel_oak"
        filterToQuery={(searchText) => ({ name: searchText })}
        sort={{ field: "name", order: "ASC" }}
      >
        <AutocompleteInput optionText="name" label="Oak" />
      </ReferenceInput>
      <ReferenceInput
        source="forestID"
        reference="barrel_forest"
        sort={{ field: "name", order: "ASC" }}
      >
        <AutocompleteInput
          optionText="name"
          filterToQuery={(searchText) => ({ name: searchText })}
          label="Forest"
        />
      </ReferenceInput>
      <TextInput source="label" label="Label" />
      <ReferenceInput
        source="typeID"
        reference="barrel_type"
        sort={{ field: "name", order: "ASC" }}
        defaultValue={0}
      >
        <AutocompleteInput
          optionText="name"
          filterToQuery={(searchText) => ({ name: searchText })}
          label="Type"
        />
      </ReferenceInput>
      <NumberInput source="year" label="Year" validate={validateYear} />
      <TextInput source="seasoning" label="Seasoning" />
      <NumberInput
        source="dryingTime"
        label="Drying Time"
        validate={validatePositiveNumber}
      />
      <BooleanInput source="toastedHead" label="Toasted Head" />
      <TextInput source="grain" label="Grain" />
    </SimpleForm>
  );
};

const handleBarrelSubmit = (data) => {
  const volumeUnit = custInfo.customerPreferences.volumeUnit;
  let modifiedValues;
  if (data.capacity && volumeUnit === "Gal") {
    modifiedValues = {
      ...data,
      capacity: ConvertGalToLitre(data.capacity),
    };
  } else {
    modifiedValues = data;
  }

  return modifiedValues;
};
export const BarrelEdit = () => (
  <Edit
    title={<BarrelAppBarTitle />}
    redirect="list"
    transform={handleBarrelSubmit}
  >
    <BarrelCreateEditForm />
  </Edit>
);

export const BarrelCreate = () => (
  <Create redirect="list" transform={handleBarrelSubmit}>
    <BarrelCreateEditForm />
  </Create>
);

const BarrelEventListFilters = [
  <SelectInput
    label="Type"
    source="type"
    choices={[
      { id: "fso2", name: "Free SO₂ Measurement" },
      { id: "addition", name: "Addition" },
    ]}
    emptyText={"All"}
  />,
  <RangePickerInput source="dateRange" label="Date" />,
];

const BarrelFSO2EventList = () => {
  const record = useRecordContext();
  return (
    <List
      perPage={100}
      pagination={<Pagination rowsPerPageOptions={[]} />}
      filter={{ barrelID: record.id }}
      filters={BarrelEventListFilters}
      title={" "}
      storeKey={false}
      empty={false}
    >
      <DataTable bulkActionButtons={false} rowClick={false}>
        <DataTable.Col source="type" label="Event" disableSort>
          <TextField source="type" label="Event" />
        </DataTable.Col>
        <DataTable.Col source="fso2" label="Free SO₂ (ppm)" disableSort>
          <MeasureOrAddField label="Free SO₂ (ppm)" />
        </DataTable.Col>
        <DataTable.Col source="datetime" label="Time" disableSort>
          <CustomDatetimeField source="datetime" label="Time" />
        </DataTable.Col>
        <DataTable.Col source="lotID" label="Lot" disableSort>
          <ReferenceField
            source="lotID"
            reference="lots"
            link="show"
            sortable={false}
          >
            <LotNameField />
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col source="notes" label="Notes" disableSort>
          <ChatBubbleTooltip source="notes" />
        </DataTable.Col>
        <DataTable.Col disableSort>
          <MeasureOrAddDetailsButton />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

const BarrelLotBarrelList = () => (
  <>
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="lotID" label="Lot" disableSort>
        <ReferenceField
          source="lotID"
          reference="lots"
          sortable={false}
          link="show"
        >
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="startTime" label="Date Added" disableSort>
        <CustomDatetimeField source="startTime" label="Date Added" />
      </DataTable.Col>
      <DataTable.Col source="endTime" label="Date Removed" disableSort>
        <CustomDatetimeField source="endTime" label="Date Removed" />
      </DataTable.Col>
      {hasScope(SCOPE.ADMIN) && (
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      )}
    </DataTable>
    <Pagination rowsPerPageOptions={[]} />
  </>
);

export const BarrelShow = () => (
  <Show title={<BarrelAppBarTitle />}>
    <TabbedShowLayout>
      <Tab label="Overview">
        <TextField source="barcode" label="Barcode" />
        <TextField source="alias" label="Alias" emptyText={"\u00A0"} />
        <VolumeField source="capacity" label="Capacity" />
        <TextField source="cooperName" label="Cooper" />
        <TextField source="toastName" label="Toast" />
        <TextField source="oakName" label="Oak" />
        <TextField source="forestName" label="Forest" />
        <TextField source="label" emptyText={"\u00A0"} />
        <TextField source="typeName" label="Type" />
        <TextField source="year" label="Year" emptyText={"\u00A0"} />
        <TextField source="seasoning" label="Seasoning" emptyText={"\u00A0"} />
        <CustomDatetimeField
          source="lastFSO2Read.datetime"
          label="Last Analyzed"
          emptyText={"\u00A0"}
        />
        <NumberField
          source="lastFSO2Read.fso2"
          options={{ maximumFractionDigits: 0 }}
          label={"Free SO\u2082 (ppm)"}
          emptyText={"\u00A0"}
        />
        <ReferenceField
          label="Lot"
          source="currentLot.id"
          reference="lots"
          link="show"
          empty={"\u00A0"}
        >
          <LotNameField />
        </ReferenceField>
        <BooleanField source="archived" label="Archived" />
      </Tab>
      <Tab label="Lot History">
        <ReferenceManyField reference="lot_barrel" target="barrelID" label="">
          <BarrelLotBarrelList />
        </ReferenceManyField>
      </Tab>
      <Tab label="FSO2 Events">
        <ReferenceManyField reference="events" target="barrelID" label="">
          <BarrelFSO2EventList />
        </ReferenceManyField>
      </Tab>
      <Tab label="Analyses">
        <ReferenceManyField
          reference="analyses"
          target="vesselID"
          sort={{ field: "timestamp", order: "DESC" }}
          label=""
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="timestamp" label="Timestamp">
              <CustomDatetimeField source="timestamp" label="Timestamp" />
            </DataTable.Col>
            <DataTable.Col source="analyte" label="Analyte" disableSort>
              <ReferenceField
                source="analysisTypeID"
                reference="analysis_types"
                sortable={false}
              >
                <TextField source="analyte" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="value" label="Value" disableSort>
              <NumberField source="value" label="Value" />
            </DataTable.Col>
            <DataTable.Col source="unit" label="Unit" disableSort>
              <ReferenceField
                source="analysisTypeID"
                reference="analysis_types"
                sortable={false}
              >
                <TextField source="unit" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="method" label="Method" disableSort>
              <TextField source="method" label="Method" />
            </DataTable.Col>
            <DataTable.Col source="lotID" label="Lot" disableSort>
              <ReferenceField source="lotID" reference="lots" sortable={false}>
                <TextField source="name" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="vesselID" label="Vessel" disableSort>
              <AliasOrBarcodeReference
                source="vesselID"
                reference="vessels"
                label="Vessel"
                sortable={false}
              />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceManyField>
      </Tab>
      <CustomTab />
      {hasScope(SCOPE.ADMIN) ? <VesselVintraceTransactionsTab /> : null}
    </TabbedShowLayout>
  </Show>
);
